import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Heart, Lock, LogOut, Save, Loader2, Calendar, MapPin,
  TrendingUp, Target, Users, Gift, Edit3, Eye, EyeOff, Plus, CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUNDRAISING_GOAL = 25000;

// Page is now live
const isPageLive = true;

const donationAmounts = [
  { value: 25, label: '$25' },
  { value: 50, label: '$50' },
  { value: 100, label: '$100' },
  { value: 250, label: '$250' },
  { value: 500, label: '$500' },
  { value: 1000, label: '$1,000' },
];

const FundraisingPage = () => {
  const [currentAmount, setCurrentAmount] = useState(0);
  const [onlineDonations, setOnlineDonations] = useState(0);
  const [manualAmount, setManualAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Donation state
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('fundraising_token') || '');
  
  // Edit mode - now for adding/subtracting amounts
  const [editMode, setEditMode] = useState(false);
  const [amountToAdd, setAmountToAdd] = useState('');
  const [amountToSubtract, setAmountToSubtract] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionType, setActionType] = useState('add'); // 'add' or 'subtract'

  // Page is always visible now
  const isVisible = isPageLive || isAdmin;

  // Fetch fundraising data
  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fundraising/total`);
      setManualAmount(response.data.manual_amount || 0);
      setOnlineDonations(response.data.online_amount || 0);
      setCurrentAmount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching fundraising data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Fast refresh for live auction - every 5 seconds
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Check if token is valid
  useEffect(() => {
    if (token) {
      axios.get(`${API}/fundraising/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        setIsAdmin(true);
      }).catch(() => {
        setIsAdmin(false);
        setToken('');
        localStorage.removeItem('fundraising_token');
      });
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      const newToken = response.data.token;
      setToken(newToken);
      localStorage.setItem('fundraising_token', newToken);
      setIsAdmin(true);
      setShowLogin(false);
      toast.success('Logged in successfully!');
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    setIsAdmin(false);
    localStorage.removeItem('fundraising_token');
    toast.success('Logged out');
  };

  // Add amount to existing total (for live auction)
  const handleAddAmount = async () => {
    if (!amountToAdd || parseFloat(amountToAdd) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    const currentToken = token || localStorage.getItem('fundraising_token');
    if (!currentToken) {
      toast.error('Please login again');
      setIsAdmin(false);
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/fundraising/add`, 
        { amount_to_add: parseFloat(amountToAdd) },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      if (response.data.success) {
        await fetchData();
        const addedAmount = amountToAdd;
        setAmountToAdd('');
        setEditMode(false);
        setActionType('add');
        toast.success(`$${parseFloat(addedAmount).toLocaleString()} added successfully!`);
      }
    } catch (error) {
      console.error('Add error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        setIsAdmin(false);
        setToken('');
        localStorage.removeItem('fundraising_token');
      } else {
        toast.error('Failed to add amount. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Subtract amount from existing total (for withdrawals)
  const handleSubtractAmount = async () => {
    if (!amountToSubtract || parseFloat(amountToSubtract) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(amountToSubtract) > manualAmount) {
      toast.error('Cannot subtract more than current cash/pledges amount');
      return;
    }
    
    const currentToken = token || localStorage.getItem('fundraising_token');
    if (!currentToken) {
      toast.error('Please login again');
      setIsAdmin(false);
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/fundraising/subtract`, 
        { amount_to_subtract: parseFloat(amountToSubtract) },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      if (response.data.success) {
        await fetchData();
        const subtractedAmount = amountToSubtract;
        setAmountToSubtract('');
        setEditMode(false);
        setActionType('add');
        toast.success(`$${parseFloat(subtractedAmount).toLocaleString()} removed successfully!`);
      }
    } catch (error) {
      console.error('Subtract error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        setIsAdmin(false);
        setToken('');
        localStorage.removeItem('fundraising_token');
      } else {
        toast.error('Failed to subtract amount. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle donation checkout
  const handleDonate = async () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    if (!amount || amount < 1) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsProcessing(true);
    try {
      // Use same format as DonatePage
      const isCustom = !!customAmount;
      const response = await axios.post(`${API}/donations/checkout`, {
        amount_key: isCustom ? 'custom' : String(amount),
        custom_amount: isCustom ? amount : null,
        category: 'fundraising',
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to create checkout. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const progressPercentage = Math.min((currentAmount / FUNDRAISING_GOAL) * 100, 100);
  const thermometerHeight = Math.max(progressPercentage, 2);

  // Show coming soon page if not visible yet and not admin
  if (!isVisible && !loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto text-center p-8"
        >
          <Calendar className="h-16 w-16 text-secondary mx-auto mb-6" />
          <h1 className="text-3xl font-bold font-['Outfit'] text-foreground mb-4">
            Coming Soon
          </h1>
          <p className="text-muted-foreground mb-6">
            Our 2026 Annual Fundraising Night will go live on March 14th at midnight.
            Join us for an exciting evening of giving!
          </p>
          <p className="text-lg font-semibold text-secondary">
            March 14, 2026 • After Isha Prayer
          </p>
          <p className="text-muted-foreground mt-2">
            54 St. Paul Crescent, Barrie, ON
          </p>
          
          {/* Admin Login */}
          <div className="mt-8">
            <button
              onClick={() => setShowLogin(!showLogin)}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mx-auto"
            >
              <Lock className="h-3 w-3" />
              Admin Access
            </button>
            
            {showLogin && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 rounded-xl bg-card border border-border"
              >
                <form onSubmit={handleLogin} className="space-y-3">
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Username"
                    className="w-full h-10 px-4 rounded-lg bg-background border border-border text-foreground text-sm"
                    required
                  />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full h-10 px-4 rounded-lg bg-background border border-border text-foreground text-sm"
                    required
                  />
                  <Button type="submit" size="sm" className="w-full" disabled={loginLoading}>
                    {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Login'}
                  </Button>
                </form>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero */}
      <section className="py-8 lg:py-12 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-secondary/20 text-secondary text-sm font-medium mb-4">
              <Calendar className="h-4 w-4" />
              March 14th, 2026 • After Isha Prayer
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground font-['Outfit'] leading-tight mb-4">
              2026 Annual <span className="text-gradient">Fundraising Night</span>
            </h1>
            <p className="text-muted-foreground leading-relaxed mb-2">
              Help us cover yearly expenses: Friday Jummah, hall rentals, and Eid celebrations for 9 months in 2026.
            </p>
            <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
              <MapPin className="h-4 w-4" />
              <span>54 St. Paul Crescent, Barrie, ON</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Left: Thermometer */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center"
            >
              {/* Goal label at top */}
              <div className="mb-4 text-center">
                <p className="text-sm text-muted-foreground">Goal</p>
                <p className="text-2xl font-bold text-foreground font-['Outfit']">
                  ${FUNDRAISING_GOAL.toLocaleString()}
                </p>
              </div>

              {/* Thermometer Body */}
              <div className="relative">
                <div className="w-24 h-72 rounded-full bg-muted/30 border-4 border-border relative overflow-hidden">
                  <div className="absolute inset-2 rounded-full bg-muted/50 overflow-hidden">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${thermometerHeight}%` }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className="absolute bottom-0 left-0 right-0 rounded-full"
                      style={{
                        background: 'linear-gradient(180deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                    </motion.div>
                  </div>
                </div>
                
                {/* Bulb at bottom */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-28 h-28 rounded-full bg-muted/30 border-4 border-border flex items-center justify-center">
                  <motion.div
                    className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{
                      background: progressPercentage > 0 
                        ? 'linear-gradient(180deg, #22c55e 0%, #15803d 100%)'
                        : 'rgba(100,100,100,0.3)',
                    }}
                  >
                    <Heart className="h-8 w-8 text-white" />
                  </motion.div>
                </div>
              </div>

              {/* Current amount */}
              <div className="mt-14 text-center">
                <p className="text-sm text-muted-foreground">Raised</p>
                <motion.p
                  key={currentAmount}
                  initial={{ scale: 1 }}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 0.3 }}
                  className="text-4xl font-bold text-green-500 font-['Outfit']"
                >
                  ${currentAmount.toLocaleString()}
                </motion.p>
                <p className="text-lg text-muted-foreground mt-1">
                  {progressPercentage.toFixed(0)}% of goal
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mt-6 w-full max-w-xs">
                <div className="p-3 rounded-xl bg-card border border-border text-center">
                  <TrendingUp className="h-5 w-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-foreground">${onlineDonations.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Online</p>
                </div>
                <div className="p-3 rounded-xl bg-card border border-border text-center">
                  <Gift className="h-5 w-5 text-secondary mx-auto mb-1" />
                  <p className="text-lg font-bold text-foreground">${manualAmount.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Cash/Pledges</p>
                </div>
              </div>

              {/* Live indicator */}
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span>Live updates every 5 seconds</span>
              </div>
            </motion.div>

            {/* Right: Donation Form */}
            <div className="space-y-6">
              {/* Donation Card */}
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h3 className="text-xl font-bold font-['Outfit'] text-foreground mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Make a Donation
                </h3>

                {/* Amount Selection */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {donationAmounts.map((amount) => (
                    <button
                      key={amount.value}
                      onClick={() => {
                        setSelectedAmount(amount.value);
                        setCustomAmount('');
                      }}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        selectedAmount === amount.value && !customAmount
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <span className="font-semibold">{amount.label}</span>
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Or enter custom amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => {
                        setCustomAmount(e.target.value);
                        setSelectedAmount(0);
                      }}
                      placeholder="Enter amount"
                      className="w-full h-12 pl-8 pr-4 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary text-foreground"
                      min="1"
                    />
                  </div>
                </div>

                {/* Donate Button */}
                <Button
                  onClick={handleDonate}
                  disabled={isProcessing}
                  className="w-full h-14 rounded-xl text-lg font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/90 glow-gold"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Heart className="mr-2 h-5 w-5" />
                      Donate ${customAmount || selectedAmount}
                    </>
                  )}
                </Button>
              </div>

              {/* What it covers */}
              <div className="p-4 rounded-xl bg-card border border-border space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  What This Covers (9 Months)
                </h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Friday Jummah expenses
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Hall rental for all programs
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Eid celebrations & gifts for kids
                  </li>
                </ul>
              </div>

              {/* Admin Toggle */}
              {!isAdmin && (
                <button
                  onClick={() => setShowLogin(!showLogin)}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mx-auto"
                >
                  <Lock className="h-3 w-3" />
                  Admin Access
                </button>
              )}
            </div>
          </div>

          {/* Admin Login Form */}
          {showLogin && !isAdmin && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 max-w-sm mx-auto p-6 rounded-xl bg-card border border-border"
            >
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Admin Login
              </h3>
              <form onSubmit={handleLogin} className="space-y-4">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  className="w-full h-12 px-4 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary text-foreground"
                  required
                />
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full h-12 px-4 pr-12 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary text-foreground"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button type="submit" className="w-full rounded-xl" disabled={loginLoading}>
                  {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Login'}
                </Button>
              </form>
            </motion.div>
          )}

          {/* Admin Panel */}
          {isAdmin && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 max-w-md mx-auto p-6 rounded-xl bg-card border-2 border-primary/50"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Edit3 className="h-5 w-5 text-primary" />
                  Live Auction Panel
                </h3>
                <button
                  onClick={handleLogout}
                  className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>

              {/* Current Total Display */}
              <div className="p-4 rounded-xl bg-background border border-border mb-4">
                <p className="text-sm text-muted-foreground">Current Cash & Pledges Total</p>
                <p className="text-3xl font-bold text-foreground">${manualAmount.toLocaleString()}</p>
              </div>

              {editMode ? (
                <div className="space-y-4">
                  {actionType === 'add' ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                          <Plus className="h-4 w-4 text-green-500" />
                          Add Amount ($)
                        </label>
                        <input
                          type="number"
                          value={amountToAdd}
                          onChange={(e) => setAmountToAdd(e.target.value)}
                          placeholder="Enter amount to add"
                          className="w-full h-12 px-4 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary text-foreground text-lg"
                          min="0"
                          step="1"
                          autoFocus
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          New total will be: ${(manualAmount + (parseFloat(amountToAdd) || 0)).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleAddAmount}
                          disabled={saving || !amountToAdd}
                          className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                          Add ${amountToAdd || '0'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditMode(false);
                            setAmountToAdd('');
                          }}
                          className="rounded-xl"
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                          <span className="text-red-500">−</span>
                          Subtract Amount ($)
                        </label>
                        <input
                          type="number"
                          value={amountToSubtract}
                          onChange={(e) => setAmountToSubtract(e.target.value)}
                          placeholder="Enter amount to remove"
                          className="w-full h-12 px-4 rounded-xl bg-background border border-red-500/50 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-foreground text-lg"
                          min="0"
                          max={manualAmount}
                          step="1"
                          autoFocus
                        />
                        <p className="text-xs text-muted-foreground mt-2">
                          New total will be: ${Math.max(0, manualAmount - (parseFloat(amountToSubtract) || 0)).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSubtractAmount}
                          disabled={saving || !amountToSubtract}
                          className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50"
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <span className="mr-2">−</span>}
                          Remove ${amountToSubtract || '0'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setEditMode(false);
                            setAmountToSubtract('');
                            setActionType('add');
                          }}
                          className="rounded-xl"
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setEditMode(true);
                      setActionType('add');
                    }}
                    className="flex-1 rounded-xl bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Bid
                  </Button>
                  <Button
                    onClick={() => {
                      setEditMode(true);
                      setActionType('subtract');
                    }}
                    variant="outline"
                    className="flex-1 rounded-xl border-red-500/50 text-red-500 hover:bg-red-500/10"
                  >
                    <span className="mr-2">−</span>
                    Remove
                  </Button>
                </div>
              )}

              <p className="text-xs text-muted-foreground mt-4 text-center">
                Online donations tracked automatically • Refreshes every 5 seconds
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Join Us Section */}
      <section className="py-12 bg-card border-y border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="h-10 w-10 text-primary mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground font-['Outfit'] mb-3">
            Join Us on March 14th
          </h2>
          <p className="text-muted-foreground text-sm mb-4">
            Come together as a community for our annual fundraising dinner.
          </p>
          <a 
            href="https://chat.whatsapp.com/BZpW0tw4MUuLAVuZmzBRDU?mode=gi_t"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" className="rounded-full">
              Join WhatsApp for Updates
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default FundraisingPage;
